import { DataAPIService, ProfileAPIService } from './AxiosAPIService';
import {
  AssignVehicleToDeviceRequest,
  AssignVehicleToDriverRequest,
  PostVehicleRequest,
  PatchVehicleRequest,
  Vehicle,
  VehicleDetails, VehicleScores,
  VehiclesStatusResponse,
  DeleteVehicleRequest,
  DeleteVehiclesRequest,
  VehiclesResponse,
  VehiclesScores,
  VehiclesScoresAverage,
  VehiclesStatisticsByDriverResponse,
  VehicleAuthorizedDrivers,
  VehicleAuthorizedDriverPostRequest,
  VehicleAuthorizedDriverPostResponse,
  VehicleAuthorizedDriverDeleteRequest,
  VehicleScoreDay,
  VehicleScoreYear,
  VehicleScoreYears,
  VehicleAlertsCount,
} from '../types/Vehicles';
import { Device } from '../types/Devices';
import { logger, normalizeQueryParams } from './SharedService';

const VEHICLES_ENDPOINT = '/fleets';

export const getVehicles = async (fleetId: string, query: string): Promise<VehiclesResponse> => {
  const { data } = await ProfileAPIService.api
    .get<VehiclesResponse>(`${VEHICLES_ENDPOINT}/${fleetId}/vehicles${query}`);
  return data;
};

export const getVehicleDetails = async (id: string, fleetId: string): Promise<VehicleDetails> => {
  const { data } = await ProfileAPIService.api
    .get<VehicleDetails>(`${VEHICLES_ENDPOINT}/${fleetId}/vehicles/${id}`);
  return data;
};

export const getVehiclesStatus = async (fleetId: string, query = ''): Promise<VehiclesStatusResponse> => {
  const { data } = await ProfileAPIService.api
    .get<VehiclesStatusResponse>(`${VEHICLES_ENDPOINT}/${fleetId}/vehicles/aggregation${query}`);
  return data;
};

export const assignVehicleToDriver = async (payload: AssignVehicleToDriverRequest): Promise<Vehicle> => {
  const { fleetId, driverId, vehicleId } = payload;

  const { data } = await ProfileAPIService.api
    .patch<Vehicle>(`${VEHICLES_ENDPOINT}/${fleetId}/vehicles/${vehicleId}`, {
    driverId,
  })
    .catch((e) => Promise.reject(e.response.data));

  return data;
};

export const assignVehicleToDevice = async (payload: AssignVehicleToDeviceRequest): Promise<Device> => {
  const { fleetId, vehicleId, deviceId } = payload;

  const { data } = await ProfileAPIService.api
    .patch<Device>(`${VEHICLES_ENDPOINT}/${fleetId}/devices/${deviceId}`, {
    vehicleId,
  })
    .catch((e) => Promise.reject(e.response.data));

  return data;
};

export const postVehicle = async (payload: PostVehicleRequest): Promise<Vehicle> => {
  const {
    manufacturer, color, model, licensePlateNumber, driverId, fleetId, category,
  } = payload;

  const { data } = await ProfileAPIService.api
    .post<Vehicle>(`${VEHICLES_ENDPOINT}/${fleetId}/vehicles`, {
    manufacturer,
    color,
    model,
    licensePlateNumber,
    driverId,
    category,
  })
    .catch((e) => Promise.reject(e.response.data));
  return data;
};

export const patchVehicle = async (payload: PatchVehicleRequest): Promise<Vehicle> => {
  const {
    id, manufacturer, color, model, licensePlateNumber, fleetId, category,
  } = payload;

  const { data } = await ProfileAPIService.api
    .patch<Vehicle>(`${VEHICLES_ENDPOINT}/${fleetId}/vehicles/${id}`, {
    manufacturer,
    color,
    model,
    licensePlateNumber,
    category,
  })
    .catch((e) => Promise.reject(e.response.data));
  return data;
};

export const deleteVehicle = async (payload: DeleteVehicleRequest): Promise<VehicleDetails> => {
  const { fleetId, vehicleId } = payload;
  const { data } = await ProfileAPIService.api
    .delete<VehicleDetails>(`${VEHICLES_ENDPOINT}/${fleetId}/vehicles/${vehicleId}`);
  return data;
};

export const deleteVehicles = async (payload: DeleteVehiclesRequest): Promise<VehicleDetails[]> => {
  const deletePromises = payload?.ids?.map(async (id) => {
    const { data } = await ProfileAPIService.api.delete<VehicleDetails>(`${VEHICLES_ENDPOINT}/${payload.fleetId}/vehicles/${id}`);
    return data;
  });

  const results = await Promise.all(deletePromises);
  return results;
};

export const getVehicleScoresById = async (
  id: string,
  fleetId: string,
  fromDate: string,
  toDate: string,
): Promise<VehicleScores> => {
  logger.info('GET', 'REQUEST', 'getVehicleScoresById', `${fleetId}/vehicles/${id}/scores?fromDate=${fromDate}&toDate=${toDate}`);
  const { data } = await DataAPIService.api
    .get<VehicleScores>(`${fleetId}/vehicles/${id}/scores?fromDate=${fromDate}&toDate=${toDate}`);
  logger.info('GET', 'RESPONSE', 'getVehicleScoresById', data);
  return data;
};

export const getVehiclesScores = async (
  fleetId: string,
  fromDate: string,
  toDate: string,
  query = '',
): Promise<VehiclesScores> => {
  logger.info('GET', 'REQUEST', 'getVehiclesScores', `${fleetId}/vehicles/scores?fromDate=${fromDate}&toDate=${toDate}${query}`);
  const { data } = await DataAPIService.api
    .get<VehiclesScores>(`${fleetId}/vehicles/scores?fromDate=${fromDate}&toDate=${toDate}${query}`);
  logger.info('GET', 'RESPONSE', 'getVehiclesScores', data);
  return data;
};

export const getVehiclesScoresAverage = async (fleetId: string, query = ''): Promise<VehiclesScoresAverage> => {
  const normalizedQuery = normalizeQueryParams(query);
  logger.info('GET', 'REQUEST', 'getVehiclesScoresAverage', `${fleetId}/vehicles/scores/average${normalizedQuery}`);
  const { data } = await DataAPIService.api
    .get<VehiclesScoresAverage>(`${fleetId}/vehicles/scores/average${normalizedQuery}`);
  logger.info('GET', 'RESPONSE', 'getVehiclesScoresAverage', data);
  return data;
};

export const getVehicleScoreDay = async (
  id: string,
  fleetId: string,
  date: string,
): Promise<VehicleScoreDay> => {
  const { data } = await DataAPIService.api
    .get<VehicleScoreDay>(`${fleetId}/vehicles/${id}/scores/day?date=${date}`);
  return data;
};

export const getVehicleScoreYear = async (
  id: string,
  fleetId: string,
  year: number,
): Promise<VehicleScoreYear> => {
  const { data } = await DataAPIService.api
    .get<VehicleScoreYear>(`${fleetId}/vehicles/${id}/scores/year?year=${year}`);
  return data;
};

export const getVehicleScoreYears = async (
  id: string,
  fleetId: string,
  from: number,
  to?: number,
): Promise<VehicleScoreYears> => {
  const toParam = to ? `&to=${to}` : '';
  const { data } = await DataAPIService.api
    .get<VehicleScoreYears>(`${fleetId}/vehicles/${id}/scores/years?from=${from}${toParam}`);
  return data;
};

export const getVehiclesStatisticsByDriver = async (fleetId: string, driverId: string, query: string): Promise<VehiclesStatisticsByDriverResponse> => {
  const { data } = await DataAPIService.api
    .get<VehiclesStatisticsByDriverResponse>(`${fleetId}/drivers/${driverId}/statistics${query}`);

  return data;
};

// Authorized drivers

export const getAuthorizedDrivers = async (fleetId: string, vehicleId: string, query?: string): Promise<VehicleAuthorizedDrivers> => {
  const { data } = await ProfileAPIService.api
    .get<VehicleAuthorizedDrivers>(`${VEHICLES_ENDPOINT}/${fleetId}/drivers-authorization${query}`);
  return data;
};

export const postAuthorizedDriver = async (payload: VehicleAuthorizedDriverPostRequest): Promise<VehicleAuthorizedDriverPostResponse> => {
  const { vehicleId, driverId, fleetId } = payload;
  const { data } = await ProfileAPIService.api
    .post<VehicleAuthorizedDriverPostResponse>(`${VEHICLES_ENDPOINT}/${fleetId}/drivers-authorization`, {
    driverId,
    vehicleId,
  })
    .catch((e) => Promise.reject(e.response.data));
  return data;
};

export const deleteAuthorizedDriver = async (payload: VehicleAuthorizedDriverDeleteRequest): Promise<VehicleAuthorizedDrivers> => {
  const { recordId, fleetId } = payload;
  const { data } = await ProfileAPIService.api
    .delete<VehicleAuthorizedDrivers>(`${VEHICLES_ENDPOINT}/${fleetId}/drivers-authorization/${recordId}`);
  return data;
};

export const getVehicleAlertsCount = async (fleetId: string, vehicleId: string, query: string): Promise<VehicleAlertsCount> => {
  const { data } = await DataAPIService.api
    .get<VehicleAlertsCount>(`${fleetId}/vehicles/${vehicleId}/alerts/count${normalizeQueryParams(query)}`);
  return data;
};
